import { memo, useEffect, useCallback, useRef, useState } from "react";
import Form from "../../components/Form.js";
import { Typography, Box, Button } from "@mui/material";
import useSelectStyles from "../../components/styles/Select.js";
import {
    getOwners,
    createMating,
    getAnimalsByGenderAndStatus,
    getEligibleGroupMatingDams,
} from "../../api/index.js";
import { useSnackbar } from "../../utils/index.js";
import Spinner from "../../components/Spinner.js";
import MatingWarningsDialog from "./MatingWarningsDialog.js";
import MatingEwesSummary from "./MatingEwesSummary.js";

const CreateMating = () => {
    const [matingCreateStep, setMatingCreateStep] = useState(1);
    const [matingType, setMatingType] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [ownersDropdownValues, setOwnersDropdownValues] = useState([]);
    const [groupForm, setGroupForm] = useState([]);
    const [fetchedOwners, setFetchedOwners] = useState([]);
    const [groupAnimalsDropdownValues, setGroupAnimalsDropdownValues] = useState([]);
    const [isWarningPopupOpen, setIsWarningPopupOpen] = useState(false);
    const [submissionWarnings, setSubmissionWarnings] = useState([]);
    const [pendingMatingValues, setPendingMatingValues] = useState(null);
    const [excludedResults, setExcludedResults] = useState(null);
    const groupFormRef = useRef(null);
    const eligibilityRequestIdRef = useRef(0);
    const ownerAnimalsRequestIdRef = useRef(0);

    const classes = useSelectStyles();
    const { success, error } = useSnackbar();

    const clearGroupExclusions = () => {
        setExcludedResults(null);
        setGroupForm((prevForm) => prevForm?.map((item) => (
            item.id === "dams" ? { ...item, excludedCodes: [] } : item
        )));
    };

    const clearGroupDamSelection = () => {
        eligibilityRequestIdRef.current += 1;
        groupFormRef.current?.setFieldValue("dams", []);
        clearGroupExclusions();
    };

    const selectEligibleGroupDams = async (owners) => {
        const formValues = groupFormRef.current?.getFormValues() || {};
        const isValidDate = (value) => value && (
            typeof value.isValid !== "function" || value.isValid()
        );
        if (!isValidDate(formValues.matingDate1) || !isValidDate(formValues.matingDate2)) {
            error("Ορίστε πρώτα έγκυρες ημερομηνίες εισόδου και εξόδου του κριού πριν επιλέξετε τις επιλέξιμες προβατίνες.");
            return;
        }

        const damOwner = owners.find((owner) => (
            `${owner?.lastName} ${owner?.firstName} - ${owner?.code}` === formValues.damOwner
        ));
        if (!damOwner) {
            error("Επιλέξτε πρώτα τη μονάδα προβατίνας.");
            return;
        }

        const requestId = ++eligibilityRequestIdRef.current;
        clearGroupExclusions();
        setIsLoading(true);
        try {
            const asDate = (value) => (typeof value.toDate === "function" ? value.toDate() : value);
            const result = await getEligibleGroupMatingDams(
                damOwner._id,
                asDate(formValues.matingDate1),
                asDate(formValues.matingDate2),
            );
            if (requestId !== eligibilityRequestIdRef.current) return;
            if (!result.success) {
                error(result.message || "Δεν ήταν δυνατός ο έλεγχος των επιλέξιμων προβατίνων.");
                return;
            }

            const eligibleCodes = result.eligibleDams.map((dam) => dam.registrationCode);
            setExcludedResults({
                animals: [...(result.excludedDams || [])].sort((a, b) => String(a.registrationCode).localeCompare(String(b.registrationCode))),
                start: asDate(formValues.matingDate1), end: asDate(formValues.matingDate2),
            });
            groupFormRef.current?.setFieldValue("dams", eligibleCodes);
            if (eligibleCodes.length === 0) {
                error("Δεν βρέθηκαν προβατίνες που να πληρούν τους ελέγχους για αυτές τις ημερομηνίες.");
            } else {
                const warningNotice = result.warningCount > 0
                    ? ` Θα εμφανιστούν προειδοποιήσεις κατά την υποβολή για ${result.warningCount} περιπτώσεις.`
                    : "";
                success(`Επιλέχθηκαν ${eligibleCodes.length} επιλέξιμες προβατίνες. Εξαιρέθηκαν ${result.excludedCount}.${warningNotice}`);
            }
        } catch (selectError) {
            error(selectError);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchData = useCallback(
        async () => {
            setIsLoading(true);
            try {
                const { owners } = await getOwners();
                setFetchedOwners(owners)

                const ownersValues = owners.map((owner) => ({
                    value: `${owner?.lastName} ${owner?.firstName} - ${owner?.code}`,
                    text: `${owner?.lastName} ${owner?.firstName} - ${owner?.code}`
                }))

                setOwnersDropdownValues(ownersValues);

                setGroupForm([
                    {
                        customType: "dropdown",
                        id: "damOwner",
                        items: ownersValues,
                        placeholder: "Μονάδα Προβατίνας",
                        onChange: async (event) => {
                            clearGroupDamSelection();
                            const requestId = ++ownerAnimalsRequestIdRef.current;
                            setGroupAnimalsDropdownValues([]);
                            setGroupForm((prevForm) => prevForm?.map((item) => (
                                item.id === "dams" ? { ...item, items: [] } : item
                            )));
                            const damOwnerValue = event.target.value;
                            const damOwner = owners.find((owner) => `${owner?.lastName} ${owner?.firstName} - ${owner?.code}` === damOwnerValue);
                            if (!damOwner) return;
                            try {
                                const { success: scs, animals } = await getAnimalsByGenderAndStatus(damOwner._id, "female", "alive");
                                if (requestId !== ownerAnimalsRequestIdRef.current) return;
                                if (!scs) {
                                    error("Σφάλμα κατά την ανάκτηση των ζώων");
                                    return;
                                }
                                const animalOptions = animals.map((animal) => ({
                                    value: animal?.registrationCode,
                                    text: animal?.registrationCode,
                                }));
                                setGroupAnimalsDropdownValues(animalOptions);
                                setGroupForm((prevForm) => prevForm?.map((item) => (
                                    item.id === "dams" ? { ...item, items: animalOptions } : item
                                )));
                            } catch (fetchError) {
                                if (requestId === ownerAnimalsRequestIdRef.current) error(fetchError);
                            }
                        },
                        required: true,
                    },
                    {
                        customType: "date-picker",
                        id: "matingDate1",
                        type: "desktop",
                        label: "Ημ/νία Εισόδου του Κριού",
                        width: "595px",
                        background: "third",
                        required: true,
                        onChange: clearGroupDamSelection,
                    },
                    {
                        customType: "date-picker",
                        id: "matingDate2",
                        type: "desktop",
                        label: "Ημ/νία Εξόδου του Κριού",
                        width: "595px",
                        background: "third",
                        required: true,
                        onChange: clearGroupDamSelection,
                    },
                    {
                        customType: "multiselect-dropdown",
                        id: "dams",
                        items: groupAnimalsDropdownValues,
                        placeholder: "Προβατίνες",
                        helperText: "Η αλλαγή εκτροφής ή ημερομηνιών ακυρώνει την επιλογή. Επιλέξτε ξανά τις επιλέξιμες προβατίνες.",
                        required: true,
                        showSelectAll: true,
                        showSelectEligible: true,
                        onSelectEligible: () => selectEligibleGroupDams(owners),
                        onChange: () => {
                            eligibilityRequestIdRef.current += 1;
                        },
                    },
                    {
                        customType: "dropdown",
                        id: "sireOwner",
                        items: owners.map((owner) => ({
                            value: `${owner?.lastName} ${owner?.firstName} - ${owner?.code}`,
                            text: `${owner?.lastName} ${owner?.firstName} - ${owner?.code}`
                        })),
                        placeholder: "Μονάδα Κριού",
                        required: true,
                    },
                    {
                        customType: "input",
                        id: "sireCode",
                        type: "text",
                        placeholder: "Πατέρας",
                        required: true,
                        helperText: "12-ψήφιος Κωδικός Γεν. Βιβλίου"
                    },
                    {
                        customType: "button",
                        id: "submit",
                        type: "submit",
                        text: "Αποθήκευση",
                    },
                ])
            } catch (err) {
                error(err);
            }

            setIsLoading(false);
        }
    )

    useEffect(() => {
        fetchData();
    }, []);

    const matingTypeForm = [
        {
            customType: "dropdown",
            id: "matingType",
            items: [
                { value: "Ατομική", text: "Ατομική" },
                { value: "Ομαδική", text: "Ομαδική" },
                { value: "Τεχνητή", text: "Τεχνητή" },
            ],
            placeholder: "Είδος σύζευξης",
            required: true,
        },
        {
            customType: "button",
            id: "submit",
            type: "submit",
            text: "Επόμενο",
        },
    ]

    const IndividualArtificialTypeForm = [
        {
            customType: "dropdown",
            id: "damOwner",
            items: ownersDropdownValues,
            placeholder: "Μονάδα Προβατίνας",
            required: true,
        },
        {
            customType: "input",
            id: "damCode",
            type: "text",
            placeholder: "Μητέρα",
            required: true,
            helperText: "12-ψήφιος Κωδικός Γεν. Βιβλίου"
        },
        {
            customType: "dropdown",
            id: "sireOwner",
            items: ownersDropdownValues,
            placeholder: "Μονάδα Κριού",
            required: true,
        },
        {
            customType: "input",
            id: "sireCode",
            type: "text",
            placeholder: "Πατέρας",
            required: true,
            helperText: "12-ψήφιος Κωδικός Γεν. Βιβλίου"
        },
        {
            customType: "date-picker",
            id: "matingDate1",
            type: "desktop",
            label: "Ημ/νία Σύζευξης",
            width: "595px",
            background: "third",
            required: true,
        },
        {
            customType: "button",
            id: "submit",
            type: "submit",
            text: "Αποθήκευση",
        },
    ]

    // const handleAddDamInput = () => {
    //     setGroupForm((prevForm) => {
    //         const addMotherIndex = prevForm.findIndex((item) => item.id === "add-mother");

    //         const newInput = {
    //             customType: "numbered-input",
    //             id: `damCode-${Date.now()}`,
    //             number: 0,
    //             type: "text",
    //             placeholder: "Προβατίνα",
    //             required: true,
    //             helperText: "12-ψήφιος Κωδικός Γεν. Βιβλίου",
    //             numberPosition: "left",
    //             showDelete: true,
    //             onDelete: (inputId) => handleDeleteDamInput(inputId)
    //         };

    //         const updatedFormContent = [
    //             ...prevForm.slice(0, addMotherIndex),
    //             newInput,
    //             ...prevForm.slice(addMotherIndex),
    //         ];

    //         return renumberDamInputs(updatedFormContent);
    //     });
    // };

    // const handleDeleteDamInput = (inputId) => {
    //     setGroupForm((prevForm) => {
    //         const filteredForm = prevForm.filter((item) => item.id !== inputId);

    //         return renumberDamInputs(filteredForm);
    //     });
    // };

    const closeWarningPopup = () => {
        setIsWarningPopupOpen(false);
        setSubmissionWarnings([]);
        setPendingMatingValues(null);
    };

    const submitMatingCreation = async (valuesToSend, confirmScheduleWarnings = false) => {
        const result = await createMating(valuesToSend, confirmScheduleWarnings);

        if (result.requiresMatingWarningConfirmation) {
            setPendingMatingValues(valuesToSend);
            setSubmissionWarnings(result.warnings || []);
            setIsWarningPopupOpen(true);
            return;
        }

        if (result.success) {
            closeWarningPopup();
            success(result.message);
            setMatingCreateStep(1);
        } else {
            error(result.message);
        }
    };

    const handleFormSubmit = async (values) => {
        setIsLoading(true);
        const valuesToSend = { ...values };
        valuesToSend.damOwner = fetchedOwners.find((owner) => `${owner?.lastName} ${owner?.firstName} - ${owner?.code}` === values.damOwner)?._id;
        valuesToSend.sireOwner = fetchedOwners.find((owner) => `${owner?.lastName} ${owner?.firstName} - ${owner?.code}` === values.sireOwner)?._id;
        if (matingType === "Ομαδική") {
            valuesToSend.damCode = values?.dams;
        } else {
            valuesToSend.damCode = [values?.damCode];
            valuesToSend.matingDate2 = null;
        }
        valuesToSend.type = matingType;
        try {
            await submitMatingCreation(valuesToSend);
        } catch (err) {
            error(err);
        }

        setIsLoading(false);
    };

    const confirmWarningSubmission = async () => {
        if (!pendingMatingValues) return;

        setIsWarningPopupOpen(false);
        setIsLoading(true);
        try {
            await submitMatingCreation(pendingMatingValues, true);
        } catch (err) {
            error(err);
        }
        setIsLoading(false);
    };

    const handleSelectMatingType = (values) => {
        clearGroupDamSelection();
        setMatingType(values.matingType);
        setMatingCreateStep(2);
    }

    return (
        <>
            <Spinner open={isLoading} />
            <MatingWarningsDialog
                open={isWarningPopupOpen}
                warnings={submissionWarnings}
                onCancel={closeWarningPopup}
                onConfirm={confirmWarningSubmission}
            />
            <Box className={classes.outerBox}>
                <Box container flexDirection="column" className={classes.innerBox}>
                    {matingCreateStep === 1 && (
                        <>
                            <Typography variant="h5" sx={{ margin: "1rem" }}>
                                Επιλογή Είδους Σύζευξης
                            </Typography>
                            <Form content={matingTypeForm} onSubmit={handleSelectMatingType} />
                        </>
                    )}
                    {matingCreateStep === 2 && (
                        <>
                            <Typography variant="h5" sx={{ margin: "1rem" }}>
                                Προσθήκη Σύζευξης
                            </Typography>
                            <Form
                                ref={matingType === "Ομαδική" ? groupFormRef : null}
                                content={matingType === "Ομαδική" ? groupForm.map((item) => item.id === "dams" ? {
                                    ...item, compactSelection: true,
                                    renderSelectionSummary: (codes) => <MatingEwesSummary selectedCodes={codes} results={excludedResults} />,
                                } : item) : IndividualArtificialTypeForm}
                                onSubmit={handleFormSubmit}
                                toResetForm={false}
                            />
                            <Button
                                onClick={() => setMatingCreateStep(1)}
                                sx={{
                                    marginTop: "1rem",
                                    padding: "0.5rem 1rem",
                                    backgroundColor: "third",
                                    color: "secondary.main",
                                    border: "none",
                                    borderRadius: "4px",
                                    cursor: "pointer",
                                    variant: "h5",
                                }}
                            >
                                Πίσω
                            </Button>
                        </>
                    )}
                </Box>
            </Box>
        </>
    )
}

export default memo(CreateMating);
